import { useEffect, useRef, useState } from 'react';
import { transportApi, studentsApi, feeConfigApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bus, Plus, Trash, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TransportRoutes = () => {
  const { t, language } = useLanguage();
  const [routes, setRoutes] = useState([]);
  const [students, setStudents] = useState([]);
  const [zones, setZones] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const nextStopKey = useRef(0);

  const blank = { name: '', bus_plate: '', driver_staff_id: '', zone: '', stops: [{ _k: -1, name: '', time: '' }], student_ids: [] };

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, fc] = await Promise.all([
        transportApi.listRoutes(),
        studentsApi.getAll(),
        feeConfigApi.get().catch(() => ({ data: {} })),
      ]);
      setRoutes(r.data);
      setStudents(s.data);
      setZones(fc.data?.transport_zones || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) { toast.error(language === 'fr' ? 'Nom obligatoire' : 'Name required'); return; }
    if (!editing.zone) { toast.error(language === 'fr' ? 'Zone d\'affectation obligatoire' : 'Assignment zone required'); return; }
    try {
      const payload = { ...editing, stops: editing.stops.filter(st => st.name) };
      const res = editing.id
        ? await transportApi.updateRoute(editing.id, payload)
        : await transportApi.createRoute(payload);
      const ridingCount = (payload.student_ids || []).length;
      const fee = res.data?.transport_fee_amount;
      if (ridingCount > 0 && fee) {
        toast.success(
          language === 'fr'
            ? `${t('routeSaved')} — ${ridingCount} élève(s) inscrits avec frais de ${Math.round(fee).toLocaleString('fr-FR')} FCFA (${res.data?.zone_name})`
            : `${t('routeSaved')} — ${ridingCount} student(s) enrolled with ${Math.round(fee).toLocaleString('fr-FR')} FCFA fee (${res.data?.zone_name})`,
          { duration: 6000 }
        );
      } else {
        toast.success(t('routeSaved'));
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('deleteRoute'))) return;
    try { await transportApi.deleteRoute(id); toast.success(t('success')); load(); }
    catch { toast.error(t('error')); }
  };

  const addStop = () => {
    const k = nextStopKey.current++;
    setEditing({ ...editing, stops: [...editing.stops, { _k: k, name: '', time: '' }] });
  };
  const updateStop = (i, key, val) => {
    const stops = [...editing.stops]; stops[i] = { ...stops[i], [key]: val };
    setEditing({ ...editing, stops });
  };
  const removeStop = (i) => setEditing({ ...editing, stops: editing.stops.filter((_, idx) => idx !== i) });

  const toggleStudent = (sid) => {
    const ids = editing.student_ids || [];
    setEditing({ ...editing, student_ids: ids.includes(sid) ? ids.filter(x => x !== sid) : [...ids, sid] });
  };

  return (
    <div className="space-y-6" data-testid="transport-routes">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Bus size={28} className="text-[#0052FF]" weight="duotone" />
          <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('transportRoutes')}</h1>
        </div>
        {!editing && (
          <Button onClick={() => setEditing(blank)} data-testid="route-new-btn">
            <Plus size={16} className="mr-1" /> {t('newRoute')}
          </Button>
        )}
      </div>

      {editing && (
        <Card className="border border-[#0052FF] shadow-none" data-testid="route-editor">
          <CardHeader><CardTitle className="font-outfit text-base">{editing.id ? t('edit') : t('newRoute')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>{t('routeName')}</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="route-name-input" /></div>
              <div><Label>{t('busPlate')}</Label><Input value={editing.bus_plate} onChange={e => setEditing({ ...editing, bus_plate: e.target.value })} data-testid="route-plate-input" /></div>
              <div>
                <Label>{language === 'fr' ? "Zone d'affectation" : 'Assignment zone'} <span className="text-red-500">*</span></Label>
                <Select value={editing.zone || ''} onValueChange={(v) => setEditing({ ...editing, zone: v })}>
                  <SelectTrigger data-testid="route-zone-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionnez une zone' : 'Select a zone'} />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.length === 0 ? (
                      <SelectItem value="__none__" disabled>{language === 'fr' ? 'Aucune zone configurée' : 'No zone configured'}</SelectItem>
                    ) : zones.map(z => (
                      <SelectItem key={z.key} value={z.key}>
                        {z.name} — {Math.round(z.amount || 0).toLocaleString('fr-FR')} FCFA
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {zones.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {language === 'fr'
                      ? 'Configurez d\'abord les zones dans Paramètres → Frais.'
                      : 'Configure zones first in Settings → Fees.'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label>{t('stops')}</Label>
              <div className="space-y-2 mt-2">
                {editing.stops.map((st, i) => (
                  <div key={st._k} className="flex gap-2 items-center" data-testid={`stop-row-${i}`}>
                    <Input className="flex-1" value={st.name} onChange={e => updateStop(i, 'name', e.target.value)} placeholder={t('stop')} />
                    <Input type="time" value={st.time} onChange={e => updateStop(i, 'time', e.target.value)} className="w-32" />
                    <Button variant="ghost" size="sm" onClick={() => removeStop(i)}><Trash size={14} className="text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addStop} data-testid="add-stop-btn">{t('addStop')}</Button>
              </div>
            </div>

            <div>
              <Label>{t('subscribedStudents')} ({(editing.student_ids || []).length})</Label>
              <p className="text-xs text-indigo-700 bg-indigo-50/60 border border-indigo-100 rounded p-2 my-2" data-testid="auto-fee-info">
                {language === 'fr'
                  ? "Chaque élève coché se verra automatiquement assigner les frais de transport selon la zone sélectionnée. Le solde devra être payé comme la scolarité et la cantine."
                  : "Each checked student will automatically be assigned the transport fees for the selected zone. Balance must be paid like tuition and canteen."}
              </p>
              <div className="max-h-48 overflow-auto border border-[#E2E8F0] rounded p-2 mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                {students.map(s => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={(editing.student_ids || []).includes(s.id)} onChange={() => toggleStudent(s.id)} data-testid={`student-cb-${s.id}`} />
                    <span className="truncate">{s.first_name} {s.last_name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={save} data-testid="route-save-btn">{t('save')}</Button>
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="route-cancel-btn">{t('cancel')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          {loading ? <p className="p-4 text-sm text-[#64748B]">{t('loading')}</p>
            : routes.length === 0 ? <p className="p-4 text-sm text-[#64748B]">{t('noRoutes')}</p>
            : (
              <ul className="divide-y divide-[#F1F5F9]">
                {routes.map(r => (
                  <li key={r.id} className="p-4 flex items-start justify-between" data-testid={`route-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <MapPin size={20} className="text-[#0052FF] mt-1" weight="duotone" />
                      <div>
                        <p className="font-semibold">{r.name} <span className="text-xs text-[#64748B]">{r.bus_plate}</span></p>
                        {r.zone ? (
                          <p className="text-xs">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium" data-testid={`route-zone-badge-${r.id}`}>
                              {(zones.find(z => z.key === r.zone)?.name) || r.zone}
                            </span>
                          </p>
                        ) : (
                          <p className="text-xs">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium border border-amber-200" data-testid={`route-zone-missing-${r.id}`}>
                              {language === 'fr' ? '⚠ Zone non définie — éditez ce circuit' : '⚠ Zone not set — edit this route'}
                            </span>
                          </p>
                        )}
                        <p className="text-xs text-[#64748B]">
                          {(r.stops || []).map(s => `${s.name} (${s.time || '–'})`).join(' → ') || t('noStops')}
                        </p>
                        <p className="text-xs text-[#94A3B8]">{(r.student_ids || []).length} {t('students').toLowerCase()}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditing(r)} data-testid={`route-edit-${r.id}`}>{t('edit')}</Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`route-delete-${r.id}`}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransportRoutes;
